import firebase from "firebase/compat/app";
import { firestore } from "../firebaseConfig";
import { Timestamp, IQueryFilter, ParameterType, IParameter } from "../types";
import { query, WhereFilterOp } from "firebase/firestore";
import { getApiToken } from "../data/api/token";
import {
  createParameter as createParameterApi,
  updateParameter as updateParameterApi,
  deleteParameter as deleteParameterApi,
} from "../data/api/parameters";

type DocumentSnapshot = firebase.firestore.DocumentSnapshot;
type SnapshotOptions = firebase.firestore.SnapshotOptions;

class Parameter implements IParameter {
  id: string;
  workspaceId: string;
  code: string;
  type: ParameterType;
  description?: string | null;
  createdAt?: Timestamp;

  static collectionName = "configuration_parameters";

  static converter = {
    toFirestore(parameter: Parameter) {
      return parameter.data();
    },
    fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions) {
      const data = snapshot.data(options) as IParameter;
      return new Parameter({ ...data, id: snapshot.id });
    },
  };

  static default() {
    return new Parameter({
      id: "",
      workspaceId: "",
      code: "",
      type: ParameterType.All
    });
  }

  static isNew(data: IParameter): boolean {
    return data.id.length === 0;
  }

  static createId() {
    return firestore.collection(Parameter.collectionName).doc().id;
  }

  constructor({
    id,
    workspaceId,
    code,
    type,
    description,
    createdAt,
  }: IParameter) {
    this.id = id;
    this.workspaceId = workspaceId;
    this.code = code;
    this.type = type;
    this.description = description || null;
    this.createdAt = createdAt;
  }

  clone() {
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this);
  }

  setData(updates: Partial<IParameter>) {
    return Object.assign(this, updates);
  }

  data() {
    return {
      id: this.id || "",
      workspaceId: this.workspaceId,
      code: this.code,
      type: this.type,
      description: this.description || null,
      createdAt: this.createdAt || null,
    };
  }

  static list = (workspaceId: string, filters?: Array<IQueryFilter>) => {

    var baseQuery = firestore
      .collection(Parameter.collectionName)
      .where("workspaceId", "==", workspaceId)

    if (filters) {
      filters.forEach((filter) => {
        baseQuery = baseQuery.where(filter.field, filter.operator as WhereFilterOp, filter.value);
      });
    }

    return query(
        baseQuery.withConverter(Parameter.converter),
    );
  };

  static create = async (workspaceId: string, data: Omit<IParameter, "id" | "createdAt">) => {
    const newParam = new Parameter({
      ...data,
      workspaceId: workspaceId,
      id: "",
    });
    try {
      const token = await getApiToken();
      await createParameterApi(token, workspaceId, newParam.data());
    } catch (e) {
      console.warn("Create configuration parameter failed with error: ", e);
      return false;
    }
    return true;
  };

  static update = async (
    parameter : Parameter,
    updates: Partial<IParameter>
  ) => {
    const updateType =
      (parameter.clone && parameter.clone()) || new Parameter({ ...parameter });

      updateType.setData({
      ...updates
    });

    const token = await getApiToken();
    const updated = await updateParameterApi(
      token,
      updateType.workspaceId,
      parameter.id,
      updateType.data(),
    );

    return new Parameter(updated);
  };

  static remove = async (parameter : Parameter) => {
    const token = await getApiToken();
    return await deleteParameterApi(token, parameter.workspaceId, parameter.id);
  };

}

export default Parameter;
